import React, { useEffect, useState, useRef } from 'react';
import './EmailConfigSettings.scss';
import { useGlobalSettings } from '../../../context/GlobalSettingsContext';
import { getAllGlobalSettingsData, createAccByEmailConfig, checkEmailConfigPassword } from '../../../services/api/GlobalSettingsApi';
import { useToast } from '../../../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../../../context/LoadingContext';
import JusticeLoader from '../../../components/JusticeLoader/JusticeLoader';

const EmailConfigSettings = () => {
    const { globalSettings, setGlobalSettings } = useGlobalSettings();
    const { showToast } = useToast();
    const [emailConfig, setEmailConfig] = useState(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'update'
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [formData, setFormData] = useState({
        accountType: 'gmail',
        email: '',
        password: '',
        port: '587',
        smtpHost: 'smtp.gmail.com',
        enabled: true
    });
    const [fetchingData, setFetchingData] = useState(true);
    const { showLoading, hideLoading } = useLoading();
    const hasFetchedInitialData = useRef(false);

    // SMTP configurations for different providers
    const smtpConfigs = {
        gmail: { host: 'smtp.gmail.com', port: '587' },
        outlook: { host: 'smtp-mail.outlook.com', port: '587' },
        yahoo: { host: 'smtp.mail.yahoo.com', port: '587' },
        office365: { host: 'smtp.office365.com', port: '587' },
        other: { host: '', port: '587' }
    };

    const navigate = useNavigate();

    // Fetch data from API on component mount only
    useEffect(() => {
        const fetchEmailConfig = async () => {
            // Only fetch if we haven't already fetched
            if (hasFetchedInitialData.current) return;
            
            try {
                setFetchingData(true);
                const response = await getAllGlobalSettingsData();

                if (response?.data?.result?.[0]?.emailConfig) {
                    const config = response.data.result[0].emailConfig;
                    setEmailConfig(config);

                    // Update global settings if not already set
                    if (!globalSettings) {
                        setGlobalSettings(response.data);
                    }
                } else {
                    setEmailConfig(null);
                }
                
                hasFetchedInitialData.current = true;
            } catch (error) {
                console.error('Error fetching email config:', error);
                showToast("Failed to fetch email configuration", "error");
                setEmailConfig(null);
            } finally {
                setFetchingData(false);
            }
        };

        fetchEmailConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only once on mount

    // Update local state when globalSettings context changes (from password updates, etc.)
    useEffect(() => {
        if (globalSettings?.result?.[0]?.emailConfig) {
            setEmailConfig(globalSettings.result[0].emailConfig);
        }
    }, [globalSettings]);

    const maskPassword = (password) => {
        return password ? '*'.repeat(password.length) : '';
    };

    const handleCreateAccount = () => {
        setModalMode('create');
        setFormData({
            accountType: 'gmail',
            email: '',
            password: '',
            port: '587',
            smtpHost: 'smtp.gmail.com',
            enabled: true
        });
        setShowEmailModal(true);
    };

    const handleUpdateData = () => {
        if (emailConfig) {
            setModalMode('update');
            setFormData({
                accountType: emailConfig.accountType || 'gmail',
                email: emailConfig.email || '',
                password: '', // Don't pre-fill password for security
                port: emailConfig.port || '587',
                smtpHost: emailConfig.smtpHost || 'smtp.gmail.com',
                enabled: emailConfig.enabled !== undefined ? emailConfig.enabled : true
            });
            setShowEmailModal(true);
        }
    };

    const handleChangePassword = () => {
        // Reset all password fields
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordModal(true);
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value };

            // Auto-set SMTP config when account type changes
            if (field === 'accountType' && smtpConfigs[value]) {
                updated.smtpHost = smtpConfigs[value].host;
                updated.port = smtpConfigs[value].port;
            }

            return updated;
        });
    };

    const handleToggle = (field) => {
        setFormData(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const handleSaveConfig = async () => {
        showLoading(modalMode === 'create' ? 'Creating email configuration...' : 'Updating email configuration...');
        try {
            // Validate required fields
            if (!formData.email || !formData.smtpHost) {
                showToast("Please fill in all required fields", "error");
                hideLoading();
                return;
            }

            // For create mode, password is required
            if (modalMode === 'create' && !formData.password) {
                showToast("Password is required", "error");
                hideLoading();
                return;
            }

            const configData = {
                email: formData.email,
                accountType: formData.accountType,
                smtpHost: formData.smtpHost,
                port: formData.port,
                enabled: formData.enabled
            };

            // Only include password if it's provided
            if (formData.password) {
                configData.password = formData.password;
            }

            // Use the same API for both create and update
            await createAccByEmailConfig(configData);

            // Refresh data after successful operation
            const updated = await getAllGlobalSettingsData();
            setGlobalSettings(updated.data);
            localStorage.setItem("globalSettings", JSON.stringify(updated));

            // Update local state directly
            if (updated.data?.result?.[0]?.emailConfig) {
                setEmailConfig(updated.data.result[0].emailConfig);
            }

            showToast(`Email configuration ${modalMode === 'update' ? 'updated' : 'created'} successfully.`, "success");

            // Close modal
            setShowEmailModal(false);

            setTimeout(() => {
                navigate('/home/displayboard');
            }, 300);
        } catch (err) {
            console.error(err);
            showToast(`Failed to ${modalMode === 'update' ? 'update' : 'create'} email configuration!`, "error");
        } finally {
            hideLoading();
        }
    };

    const handleChangePasswordSave = async () => {
        // Validate all password fields
        if (!oldPassword.trim()) {
            showToast("Please enter your old password", "error");
            return;
        }

        if (!newPassword.trim()) {
            showToast("Please enter a new password", "error");
            return;
        }

        if (!confirmPassword.trim()) {
            showToast("Please confirm your new password", "error");
            return;
        }

        if (newPassword !== confirmPassword) {
            showToast("New password and confirm password do not match", "error");
            return;
        }

        if (newPassword.length < 6) {
            showToast("Password must be at least 6 characters long", "error");
            return;
        }

        showLoading('Verifying and updating password...');
        try {
            // First, verify the old password
            const verifyResponse = await checkEmailConfigPassword({ password: oldPassword });
            

            if (verifyResponse.data?.match !== true) {
                // Old password is incorrect - don't proceed with update
                showToast("Incorrect old password. Please try again.", "error");
                hideLoading();
                return;
            }

            // Old password is correct, proceed with update
            const configData = {
                email: emailConfig.email,
                accountType: emailConfig.accountType,
                password: newPassword,
                smtpHost: emailConfig.smtpHost,
                port: emailConfig.port,
                enabled: emailConfig.enabled
            };

            await createAccByEmailConfig(configData);

            const updated = await getAllGlobalSettingsData();
            setGlobalSettings(updated.data);
            localStorage.setItem("globalSettings", JSON.stringify(updated));

            // Update local state directly to show updated password
            if (updated.data?.result?.[0]?.emailConfig) {
                setEmailConfig(updated.data.result[0].emailConfig);
            }

            showToast("Password updated successfully.", "success");
            setShowPasswordModal(false);
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            console.error(err);
            showToast("Failed to update password. Please try again.", "error");
        } finally {
            hideLoading();
        }
    };

    // Show loading state while fetching data
    if (fetchingData) {
        return (
            <section className='email-config-section'>
                {/* <h3 className='email-config-title'>Email Configuration Settings</h3> */}
                <JusticeLoader message="Loading email configuration..." type="gavel" />
            </section>
        );
    }

    return (
        <>
            <section className="email-config-section">
                {/* <h3 className="email-config-title">Email Configuration Settings</h3> */}

                <div className="settings-container">
                    {emailConfig ? (
                        <>
                            {/* Account Information Card */}
                            <div className="settings-card">
                                <h4 className="card-title">Account Information</h4>
                                <div className="form-grid">
                                    <div className="form-group-email-config">
                                        <label>Account Type</label>
                                        <div className="display-value account-type">
                                            {emailConfig.accountType}
                                        </div>
                                    </div>
                                    
                                    <div className="form-group-email-config">
                                        <label>Email Address</label>
                                        <div className="display-value email-address">
                                            {emailConfig.email}
                                        </div>
                                    </div>

                                      <div className="form-group-email-config">
                                        <label>Status</label>
                                        <div className={`display-value status ${emailConfig.enabled ? 'enabled' : 'disabled'}`}>
                                            {emailConfig.enabled ? 'Enabled' : 'Disabled'}
                                        </div>
                                    </div>
                                    
                                    <div className="form-group-email-config">
                                        <label>Password</label>
                                        <div className="display-value password-mask">
                                            {maskPassword(emailConfig.password)}
                                        </div>
                                        <button
                                            type="button"
                                            className="change-password-btn"
                                            onClick={handleChangePassword}
                                        >
                                            Change Password
                                        </button>
                                    </div>
                                    
                                  
                                </div>
                            </div>

                            {/* SMTP Configuration Card */}
                            <div className="settings-card">
                                <h4 className="card-title">SMTP Configuration</h4>
                                <div className="form-grid">
                                    <div className="form-group-email-config">
                                        <label>SMTP Host</label>
                                        <div className="display-value">
                                            {emailConfig.smtpHost}
                                        </div>
                                    </div>
                                    
                                    <div className="form-group-email-config">
                                        <label>Port</label>
                                        <div className="display-value">
                                            {emailConfig.port}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="settings-card no-config-card">
                            <div className="no-config-content">
                                <div className="no-config-icon">
                                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                        <polyline points="22,6 12,13 2,6" />
                                    </svg>
                                </div>
                                <h4>No Email Configuration Found</h4>
                                <p>Set up your email configuration to enable email notifications and communications.</p>
                                <button
                                    type="button"
                                    className="create-account-btn"
                                    onClick={handleCreateAccount}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Create Email Configuration
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                {emailConfig && (
                    <div className="action-buttons">
                        <button
                            className="cancel-btn"
                            onClick={() => navigate('/home/displayboard')}
                        >
                            Cancel
                        </button>
                        <button
                            className="save-btn"
                            onClick={handleUpdateData}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            Update Configuration
                        </button>
                    </div>
                )}

                {/* Change Password Modal */}
                {showPasswordModal && (
                    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowPasswordModal(false)}>
                        <div className="modal password-modal">
                            <div className="modal-header">
                                <h4>Change Password</h4>
                                <button
                                    type="button"
                                    className="close-btn"
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setOldPassword('');
                                        setNewPassword('');
                                        setConfirmPassword('');
                                    }}
                                    aria-label="Close modal"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>

                            <div className="modal-body">
                                <div className="settings-card">
                                    <div className="step-info">
                                        <p>Enter your current password and choose a new password to update your email configuration.</p>
                                    </div>
                                    <div className="form-group-email-config">
                                        <label>Current Password</label>
                                        <input
                                            type="password"
                                            value={oldPassword}
                                            onChange={(e) => setOldPassword(e.target.value)}
                                            placeholder="Enter your current password"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="form-group-email-config">
                                        <label>New Password</label>
                                        <input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="Enter new password"
                                        />
                                    </div>
                                    <div className="form-group-email-config">
                                        <label>Confirm New Password</label>
                                        <input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Confirm new password"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && oldPassword.trim() && newPassword.trim() && confirmPassword.trim()) {
                                                    handleChangePasswordSave();
                                                }
                                            }}
                                        />
                                    </div>
                                    {newPassword && confirmPassword && newPassword !== confirmPassword && (
                                        <div className="password-mismatch-error">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="15" y1="9" x2="9" y2="15" />
                                                <line x1="9" y1="9" x2="15" y2="15" />
                                            </svg>
                                            Passwords do not match
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setOldPassword('');
                                        setNewPassword('');
                                        setConfirmPassword('');
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="save-btn"
                                    onClick={handleChangePasswordSave}
                                    disabled={!oldPassword.trim() || !newPassword.trim() || !confirmPassword.trim() || newPassword !== confirmPassword}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20,6 9,17 4,12" />
                                    </svg>
                                    Save Password
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Email Configuration Modal */}
                {showEmailModal && (
                    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowEmailModal(false)}>
                        <div className="modal email-config-modal">
                            <div className="modal-header">
                                <h4>{modalMode === 'create' ? 'Create Email Configuration' : 'Update Email Configuration'}</h4>
                                <button
                                    type="button"
                                    className="close-btn"
                                    onClick={() => setShowEmailModal(false)}
                                    aria-label="Close modal"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>

                            <div className="modal-body">
                                <div className="settings-container">
                                    {/* Account Setup Card */}
                                    <div className="settings-card">
                                        <h4 className="card-title">Account Setup</h4>
                                        <div className="form-grid">
                                            <div className="form-group-email-config">
                                                <label>Email Provider</label>
                                                <select
                                                    value={formData.accountType}
                                                    onChange={(e) => handleFormChange('accountType', e.target.value)}
                                                >
                                                    <option value="gmail">Gmail</option>
                                                    <option value="outlook">Outlook</option>
                                                    <option value="yahoo">Yahoo</option>
                                                    <option value="office365">Office 365</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>

                                            <div className="form-group-email-config">
                                                <label>Email Address</label>
                                                <input
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={(e) => handleFormChange('email', e.target.value)}
                                                    placeholder="your.email@example.com"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group-email-config">
                                            <label>
                                                Password {modalMode === 'update' && '(leave empty to keep current)'}
                                            </label>
                                            <input
                                                type="password"
                                                value={formData.password}
                                                onChange={(e) => handleFormChange('password', e.target.value)}
                                                placeholder={modalMode === 'create' ? 'Enter your password' : 'Enter new password'}
                                                required={modalMode === 'create'}
                                            />
                                        </div>
                                    </div>

                                    {/* SMTP Configuration Card */}
                                    <div className="settings-card">
                                        <h4 className="card-title">SMTP Configuration</h4>
                                        <div className="form-grid">
                                            <div className="form-group-email-config">
                                                <label>SMTP Host</label>
                                                <input
                                                    type="text"
                                                    value={formData.smtpHost}
                                                    onChange={(e) => handleFormChange('smtpHost', e.target.value)}
                                                    placeholder="smtp.gmail.com"
                                                    required
                                                />
                                            </div>

                                            <div className="form-group-email-config">
                                                <label>Port</label>
                                                <input
                                                    type="number"
                                                    value={formData.port}
                                                    onChange={(e) => handleFormChange('port', e.target.value)}
                                                    placeholder="587"
                                                    required
                                                    className="no-spinner"
                                                />
                                            </div>
                                        </div>

                                        {formData.accountType !== 'other' && (
                                            <div className="smtp-info">
                                                <div className="info-header">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="12" r="10" />
                                                        <path d="M9,9h6v6H9Z" />
                                                    </svg>
                                                    Auto-configured SMTP Settings
                                                </div>
                                                <div className="info-content">
                                                    <div className="info-item">
                                                        <span className="label">Host:</span>
                                                        <span className="value">{formData.smtpHost}</span>
                                                    </div>
                                                    <div className="info-item">
                                                        <span className="label">Port:</span>
                                                        <span className="value">{formData.port}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Additional Settings Card */}
                                    <div className="settings-card">
                                        <h4 className="card-title">Additional Settings</h4>
                                        <div className="toggle-row">
                                            <label>Enable Configuration</label>
                                            <div className="toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.enabled}
                                                    onChange={() => handleToggle('enabled')}
                                                    id="enabled"
                                                />
                                                <label htmlFor="enabled" className="toggle-slider"></label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setShowEmailModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="save-btn"
                                    onClick={handleSaveConfig}
                                    disabled={!formData.email || !formData.smtpHost || (modalMode === 'create' && !formData.password)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20,6 9,17 4,12" />
                                    </svg>
                                    {modalMode === 'create' ? 'Create Configuration' : 'Update Configuration'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </>
    );
};

export default EmailConfigSettings;